import React, { useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Stack,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import {
  Routes as RRDRoutes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import { usePermissions, hasPermission } from '@billda/permissions-ui';
import { Routes as AppRoutes } from '../../routes';
import { DEFAULT_AREA, visibleAreas } from './settings/settingsAreas';
import { GeneralArea } from './settings/GeneralArea';
import { OrganisationArea } from './settings/OrganisationArea';
import { SecurityArea } from './settings/SecurityArea';
import { AutoSubmitArea } from '../../features/organisation/components/AutoSubmitArea';

/**
 * Full-page settings shell: hides the main portal nav, owns its own
 * sub-navigation, and renders each area under nested /settings/* routes.
 */
export const Settings: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isNarrow = useMediaQuery(theme.breakpoints.down('md'));

  const permissions = usePermissions();
  const areas = useMemo(
    () => visibleAreas(id => hasPermission(permissions, id)),
    [permissions]
  );

  const activeArea =
    areas.find(area => location.pathname.startsWith(area.path))?.id ??
    DEFAULT_AREA;

  const handleClose = () => {
    navigate(AppRoutes.root);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 4,
        }}
      >
        <Typography variant="h4" component="h1">
          Settings
        </Typography>
        <IconButton
          aria-label="Close settings"
          onClick={handleClose}
          data-testid="settings-close"
        >
          <Close />
        </IconButton>
      </Box>

      {isNarrow ? (
        <Box sx={{ mb: 3 }}>
          <Tabs
            value={activeArea}
            onChange={(_, id) => navigate(`/settings/${id}`)}
            variant="scrollable"
            scrollButtons="auto"
            data-testid="settings-nav"
          >
            {areas.map(area => (
              <Tab
                key={area.id}
                value={area.id}
                label={area.label}
                data-testid={area.testId}
              />
            ))}
          </Tabs>
        </Box>
      ) : (
        <Stack direction="row" spacing={4} alignItems="flex-start">
          <List data-testid="settings-nav" sx={{ width: 220, flexShrink: 0 }}>
            {areas.map(area => (
              <ListItemButton
                key={area.id}
                selected={area.id === activeArea}
                onClick={() => navigate(area.path)}
                data-testid={area.testId}
              >
                <ListItemText primary={area.label} />
              </ListItemButton>
            ))}
          </List>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <SettingsRoutes />
          </Box>
        </Stack>
      )}

      {isNarrow && <SettingsRoutes />}
    </Container>
  );
};

const SettingsRoutes: React.FC = () => (
  <RRDRoutes>
    <Route index element={<Navigate to={DEFAULT_AREA} replace />} />
    <Route path="general" element={<GeneralArea />} />
    <Route path="organisation" element={<OrganisationArea />} />
    <Route path="auto-submit" element={<AutoSubmitArea />} />
    <Route path="security" element={<SecurityArea />} />
    <Route path="*" element={<Navigate to={DEFAULT_AREA} replace />} />
  </RRDRoutes>
);
